import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc, query, where, deleteDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/LessonDetailPage.css";

function LessonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [konular, setKonular] = useState([]);
  const [username, setUsername] = useState('');
  const [seciliKonuIndex, setSeciliKonuIndex] = useState(null);
  const [kategoriSecimi, setKategoriSecimi] = useState('');
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        const savedName = localStorage.getItem('username');
        if (savedName) {
           setUsername(savedName);
        }
      } else {
        setUserId(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchTopics = async () => {
      if (!userId) return;

      try {
        const q = query(collection(db, "topics"), where("userId", "==", userId), where("lessonId", "==", id));
        const querySnapshot = await getDocs(q);
        const topics = [];
        querySnapshot.forEach((doc) => {
          topics.push({ id: doc.id, ...doc.data() });
        });

        if (topics.length === 0) {
          const defaultTopics = Array.from({ length: 5 }, (_, i) => ({
            ad: `Konu ${i + 1}`,
            tik: false,
            tekrar: 0,
            soru: 0,
          }));
          for (const topic of defaultTopics) {
            const topicData = {
              userId,
              lessonId: id,
              ...topic,
            };
            const docRef = await addDoc(collection(db, "topics"), topicData);
            topics.push({ id: docRef.id, ...topicData });
          }
        }
        setKonular(topics);
      } catch (err) {
        setError(`Konular çekilemedi: ${err.message}`);
      }
    };

    if (!loading) {
      fetchTopics();
    }
  }, [userId, id, loading, navigate]);

  const kategoriler = ['Günlük', 'Haftalık', 'Aylık', 'Uzun Vadeli'];

  const handleChange = async (index, field, value) => {
    const yeni = [...konular];
    yeni[index][field] = field === 'tik' ? value.target.checked : parseInt(value.target.value);
    setKonular(yeni);

    try {
      const topicRef = doc(db, "topics", yeni[index].id);
      await updateDoc(topicRef, { [field]: yeni[index][field] });
    } catch (err) {
      setError(`Konu güncellenemedi: ${err.message}`);
    }
  };

  const handleAdChange = async (index, e) => {
    const yeni = [...konular];
    yeni[index].ad = e.target.value;
    setKonular(yeni);

    try {
      const topicRef = doc(db, "topics", yeni[index].id);
      await updateDoc(topicRef, { ad: e.target.value });
    } catch (err) {
      setError(`Konu adı güncellenemedi: ${err.message}`);
    }
  };

  const handleDelete = async (index) => {
    const topicId = konular[index].id;
    const yeni = [...konular];
    yeni.splice(index, 1);
    setKonular(yeni);

    try {
      await deleteDoc(doc(db, "topics", topicId));
    } catch (err) {
      setError(`Konu silinemedi: ${err.message}`);
    }
  };

  const handleAdd = async () => {
    const newTopic = {
      userId,
      lessonId: id,
      ad: `Yeni Konu`,
      tik: false,
      tekrar: 0,
      soru: 0,
    };

    try {
      const docRef = await addDoc(collection(db, "topics"), newTopic);
      setKonular([...konular, { id: docRef.id, ...newTopic }]);
    } catch (err) {
      setError(`Yeni konu eklenemedi: ${err.message}`);
    }
  };

  const onKategoriSecimi = async (kategori) => {
    if (!kategori) return;

    const konuAd = konular[seciliKonuIndex].ad;
    const goal = {
      userId,
      text: `${id} - ${konuAd}`,
      category: kategori,
      done: false,
      progress: 0,
    };

    try {
      const ref = doc(collection(db, "goals"));
      await setDoc(ref, goal);
      setSeciliKonuIndex(null);
      setKategoriSecimi('');
      setError("Hedef başarıyla eklendi! 🎉");
    } catch (err) {
      setError(`Hedef eklenirken hata oluştu: ${err.message}`);
    }
  };

  const getDurum = (k) => {
    const toplam = k.tekrar + k.soru;
    if (!k.tik) return 'Beklemede';
    if (toplam >= 10) return 'Bu iş bitti';
    if (toplam >= 8) return 'Hazır';
    if (toplam >= 6) return 'Neredeyse hazır';
    if (toplam >= 4) return 'Parlatıyor';
    if (toplam >= 2) return 'Cilalıyor';
    return 'Başladı';
  };

  const durumRenk = {
    Beklemede: '#666',
    Başladı: '#81d4fa',
    Cilalıyor: '#64b5f6',
    Parlatıyor: '#ffd600',
    'Neredeyse hazır': '#ffab00',
    Hazır: '#00c853',
    'Bu iş bitti': '#4caf50',
  };

  const durumSirasi = [
    'Beklemede',
    'Başladı',
    'Cilalıyor',
    'Parlatıyor',
    'Neredeyse hazır',
    'Hazır',
    'Bu iş bitti',
  ];

  const durumSayilari = {
    Beklemede: 0,
    Başladı: 0,
    Cilalıyor: 0,
    Parlatıyor: 0,
    'Neredeyse hazır': 0,
    Hazır: 0,
    'Bu iş bitti': 0,
  };

  konular.forEach((k) => {
    const toplam = k.tekrar + k.soru;
    if (!k.tik) {
      durumSayilari['Beklemede']++;
      return;
    }
    if (toplam >= 0) durumSayilari['Başladı']++;
    if (toplam >= 2) durumSayilari['Cilalıyor']++;
    if (toplam >= 4) durumSayilari['Parlatıyor']++;
    if (toplam >= 6) durumSayilari['Neredeyse hazır']++;
    if (toplam >= 8) durumSayilari['Hazır']++;
    if (toplam >= 10) durumSayilari['Bu iş bitti']++;
  });

  const toplamKonu = konular.length;

  if (loading) {
    return <div style={{ textAlign: 'center', color: '#f0f0f0' }}>Yükleniyor...</div>;
  }

  return (
    <motion.div
      className="lesson-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="header-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <h1 className="title">
          📘 {id.charAt(0).toUpperCase() + id.slice(1)} - {username}
        </h1>
        <div className="progress-summary">
          <div className="progress-item">
            <span className="progress-label">Toplam Konu:</span>
            <span className="progress-value">{toplamKonu}</span>
          </div>
          <div className="progress-item">
            <span className="progress-label">Tamamlanan:</span>
            <span className="progress-value">{durumSayilari['Bu iş bitti']}</span>
          </div>
          <div className="progress-item">
            <span className="progress-label">İlerleme:</span>
            <span className="progress-value">{((durumSayilari['Bu iş bitti'] / toplamKonu) * 100).toFixed(1)}%</span>
          </div>
        </div>
      </motion.div>
      <motion.div
        className="cards-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <AnimatePresence>
          {konular.map((k, i) => {
            const durum = getDurum(k);
            return (
              <motion.div
                key={k.id || i}
                className="card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                whileHover={{ scale: 1.03, rotate: 1 }}
                whileTap={{ scale: 0.97 }}
                transition={{ delay: i * 0.1, duration: 0.3 }}
              >
                <div className="card-header">
                  <motion.input
                    type="text"
                    value={k.ad}
                    onChange={(e) => handleAdChange(i, e)}
                    className="input-text"
                    whileFocus={{ borderColor: "#00e676" }}
                    transition={{ duration: 0.2 }}
                  />
                  <div className="status-badge" style={{ backgroundColor: durumRenk[durum] }}>
                    {durum}
                  </div>
                </div>
                <div className="card-body">
                  <div className="info-row">
                    <div className="checkbox-container">
                      <label className="label">
                        <input
                          type="checkbox"
                          checked={k.tik}
                          onChange={(e) => handleChange(i, 'tik', e)}
                          className="checkbox"
                        />
                        <span>Çalışıldı</span>
                      </label>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="number-input-container">
                      <span className="small-text">🔁 Tekrar:</span>
                      <motion.input
                        type="number"
                        value={k.tekrar}
                        onChange={(e) => handleChange(i, 'tekrar', e)}
                        className="number-input"
                        whileFocus={{ borderColor: "#00e676" }}
                        transition={{ duration: 0.2 }}
                      />
                    </div>
                    <div className="number-input-container">
                      <span className="small-text">📝 Soru:</span>
                      <motion.input
                        type="number"
                        value={k.soru}
                        onChange={(e) => handleChange(i, 'soru', e)}
                        className="number-input"
                        whileFocus={{ borderColor: "#00e676" }}
                        transition={{ duration: 0.2 }}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-footer">
                  <motion.button
                    onClick={() => setSeciliKonuIndex(i)}
                    className="action-button"
                    whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    🎯 Hedefe Ekle
                  </motion.button>
                  <motion.button
                    onClick={() => handleDelete(i)}
                    className="delete-button"
                    whileHover={{ scale: 1.05, backgroundColor: "#ff4b2b" }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    Sil
                  </motion.button>
                </div>
                <AnimatePresence>
                  {seciliKonuIndex === i && (
                    <motion.div
                      className="select-container"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.select
                        value={kategoriSecimi}
                        onChange={(e) => {
                          setKategoriSecimi(e.target.value);
                          onKategoriSecimi(e.target.value);
                        }}
                        className="select"
                        whileFocus={{ borderColor: "#00e676" }}
                        transition={{ duration: 0.2 }}
                      >
                        <option value="">Zaman seç...</option>
                        {kategoriler.map((kat) => (
                          <option key={kat} value={kat}>
                            {kat}
                          </option>
                        ))}
                      </motion.select>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
      <motion.div
        className="add-button-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <motion.button
          onClick={handleAdd}
          className="add-button"
          whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
        >
          + Yeni Konu Ekle
        </motion.button>
      </motion.div>
      <motion.div
        className="analysis-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <h3 className="analysis-title">📊 Ders İçi Durum Analizi</h3>
        <div className="analysis-grid">
          {durumSirasi.map((durum, index) => {
            const adet = durumSayilari[durum] || 0;
            const oran = toplamKonu ? (adet / toplamKonu) * 100 : 0;
            const emoji = {
              'Beklemede': '⏳',
              'Başladı': '🚀',
              'Cilalıyor': '✨',
              'Parlatıyor': '💎',
              'Neredeyse hazır': '📈',
              'Hazır': '✅',
              'Bu iş bitti': '🏆',
            }[durum];
            return (
              <motion.div
                key={durum}
                className="analysis-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.15)" }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
              >
                <div className="analysis-card-header">
                  <span className="analysis-status">
                    {emoji} {durum}
                  </span>
                  <span className="analysis-count">{adet} konu</span>
                </div>
                <div className="progress-bar-container">
                  <motion.div
                    className="progress-bar"
                    style={{ backgroundColor: durumRenk[durum] }}
                    initial={{ width: 0 }}
                    animate={{ width: `${oran}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                  <span className="progress-percentage">{Math.round(oran)}%</span>
                </div>
                <div className="analysis-icon">
                  <span>{emoji}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.p
            className="error-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default LessonDetailPage;